import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'
// SECURITY (sec/s0f-token-storage): all token read/write/clear now routes
// through the single swappable tokenStorage module (see its header for the
// httpOnly-cookie migration TODO). The interceptor logic below is unchanged.
import * as tokenStorage from '@/lib/tokenStorage'
// PERM-DENIED-AR-01: localise backend "Missing required permission: <code>"
// 403s at the single choke point every toast reads from.
import {
  businessRuleMessage, matchBusinessRule,
  parseMissingPermission, permissionDeniedMessage, type UiLang,
} from '@/lib/permissionLabels'
// P2-1 (#1064): the 401 forced-logout path clears localStorage for EVERY tab
// (tokens are shared there), but other tabs only found out on their next
// request -- until then they kept a live-looking zustand store, including a
// permission set that had outlived its session. Broadcasting the logout makes
// them reset immediately. Imported from lib/authBroadcast rather than
// stores/authStore because authStore imports THIS module (cycle).
import { broadcastAuthEvent, tokenSubject } from '@/lib/authBroadcast'
// PROD-SESSION-HARD-NAV-01 (F1.3): the same-tab hand-off to the auth store.
// A dependency-free registry both sides import, for the same cycle reason as
// authBroadcast above -- and so the store never has to read this module at
// module scope. See lib/authTokenSync.ts.
import { publishAuthCleared, publishTokenPair } from '@/lib/authTokenSync'
// SESSION-LOGOUT-WHILE-POLLING-01. Three dependency-free modules, imported for
// the same one-way-arrow reason as the two above:
//   H2 refreshLock        -- one rotation at a time ACROSS TABS.
//   H3 connectionState    -- "reconnecting" is not "your session ended".
//   H1 backgroundActivity -- a live background job counts as presence.
import { RefreshLockTimeoutError, withRefreshLock } from '@/lib/refreshLock'
import { publishOnline, publishReconnecting } from '@/lib/connectionState'
import { publishBackgroundActivity } from '@/lib/backgroundActivity'

// ============================================================================
// Configuration
// ============================================================================

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8001/api/v1'

// Re-exported for backward compatibility (__authInternals consumers). The
// canonical definition now lives in tokenStorage.ts.
const STORAGE_KEYS = tokenStorage.TOKEN_STORAGE_KEYS

const LOOP_DETECTION = {
  STORAGE_KEY: 'login_redirect_timestamps',
  WINDOW_MS: 2000, // 2-second sliding window
  MAX_REDIRECTS: 3, // 3 redirects in window = loop
} as const

// ============================================================================
// Axios instance
// ============================================================================

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

// ============================================================================
// Loop Detection — fixes the original Safari/Desktop infinite-redirect bug
// ============================================================================

function recordRedirectAttempt(): boolean {
  // Returns true if a redirect loop is detected (caller should ABORT redirect)
  try {
    const now = Date.now()
    const raw = sessionStorage.getItem(LOOP_DETECTION.STORAGE_KEY)
    const timestamps: number[] = raw ? JSON.parse(raw) : []

    const recent = timestamps.filter((t) => now - t < LOOP_DETECTION.WINDOW_MS)
    recent.push(now)

    sessionStorage.setItem(LOOP_DETECTION.STORAGE_KEY, JSON.stringify(recent))
    return recent.length >= LOOP_DETECTION.MAX_REDIRECTS
  } catch {
    // sessionStorage may be unavailable (iOS Safari private mode) — fail open
    return false
  }
}

function clearLoopHistory(): void {
  try {
    sessionStorage.removeItem(LOOP_DETECTION.STORAGE_KEY)
  } catch {
    /* noop */
  }
}

// ============================================================================
// Auth helpers — delegate to the centralized tokenStorage backend, decoupled
// from the zustand store. (Thin wrappers preserve the existing call sites and
// the __authInternals export surface.)
// ============================================================================

function getAccessToken(): string | null {
  return tokenStorage.getAccessToken()
}

function getRefreshToken(): string | null {
  return tokenStorage.getRefreshToken()
}

function setTokens(accessToken: string, refreshToken: string | null): void {
  tokenStorage.setTokens(accessToken, refreshToken)
  // A live session exists again (a login, or a rotation via publishTokens), so
  // the pending-navigation latch from any earlier forced logout is stale --
  // the NEXT forced logout must be free to redirect (#1081 P2-1). Safe to do
  // here: nothing writes tokens during the 401 burst the latch exists for,
  // because that burst is precisely the case where the refresh FAILED.
  logoutRedirectPending = false
}

// ----------------------------------------------------------------------------
// Token publication (PROD-SESSION-HARD-NAV-01 / F1.3)
// ----------------------------------------------------------------------------
// A rotated pair used to be written to the raw localStorage keys and NOWHERE
// else: performRefresh() called setTokens() and returned. The zustand store
// kept serving the token it was last handed by setAuth()/its own refresh, and
// re-persisted that stale value on every later set(). The mirror in
// `auth-storage` therefore drifted permanently out of step with
// `access_token` -- and because onRehydrateStorage reads the STORE's token to
// decide whether a boot-time refresh is due, a drifted (expired) mirror made
// every subsequent hard load fire one.
//
// Every successful refresh now goes through publishTokens(), which updates all
// three places exactly once:
//   1. the raw localStorage keys   (what the request interceptor reads)
//   2. THIS tab's zustand store    (via lib/authTokenSync -- a direct call,
//      because BroadcastChannel never delivers to the sender)
//   3. the OTHER tabs              (via the broadcast)
//
// (2) goes through the standalone registry rather than a listener slot on
// `__authInternals` so that the store can subscribe at module scope without
// touching THIS module's export surface -- see lib/authTokenSync.ts.
// ----------------------------------------------------------------------------

function publishTokens(accessToken: string, refreshToken: string | null): void {
  setTokens(accessToken, refreshToken)
  publishTokenPair({ accessToken, refreshToken })
  broadcastAuthEvent({
    type: 'TOKENS_UPDATED',
    accessToken,
    refreshToken,
    sub: tokenSubject(accessToken),
  })
}

function clearAllAuth(): void {
  // PART_4_LOOP_FIX: tokenStorage.clearAllAuth() also clears the zustand persist
  // blob to prevent /login from seeing stale user state and bouncing back to
  // /dashboard.
  tokenStorage.clearAllAuth()
  // ...and tell the store, so a fetch that was already in flight cannot write
  // the ended session's answer back into it -- and back into the persist blob
  // we just wiped (review #1081 R4). A direct call, because this tab never
  // receives its own LOGGED_OUT broadcast.
  publishAuthCleared()
}

// PROD-SESSION-HARD-NAV-01 / F1.1: every request queued behind a failed
// refresh now lands in the interceptor's own catch (they used to reject
// silently while only the queue leader force-logged-out), so N concurrent
// 401s would mean N calls to window.location.replace(). The navigation is
// asynchronous, so the pathname guard below does NOT dedupe them -- this latch
// does. One hard load = at most one redirect.
//
// REVIEW FIX (#1081 P2-1): the latch first shipped wrapped around the WHOLE
// function and was never reset, which made forceLogoutToLoginPage() a one-shot
// for the lifetime of the JS context. A user who was bounced once, logged back
// in as someone else and was bounced again kept that second session's tokens
// in localStorage, and the other tabs were never told. So the latch now covers
// ONLY the navigation, and only while one is genuinely pending -- the state
// teardown and the broadcast run on every call, unconditionally.
let logoutRedirectPending = false

function forceLogoutToLoginPage(): void {
  // Always, however many times we are called: the point of this function is
  // that the credentials in this context are dead.
  clearAllAuth()
  clearLoopHistory()
  // Tell the other tabs before navigating away: this context is about to be
  // replaced, and BroadcastChannel never delivers to the sender anyway.
  broadcastAuthEvent({ type: 'LOGGED_OUT' })

  // The navigation, and only the navigation, is deduped.
  if (logoutRedirectPending) return
  if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
    logoutRedirectPending = true
    // replace() prevents back-button from re-triggering the loop
    window.location.replace('/login')
  }
}

// ============================================================================
// Single-flight refresh — only one /auth/refresh call in flight at a time
// ============================================================================

interface QueueItem {
  resolve: (token: string) => void
  reject: (err: unknown) => void
}

let isRefreshing = false
let refreshQueue: QueueItem[] = []

function flushQueue(error: unknown, token: string | null): void {
  refreshQueue.forEach(({ resolve, reject }) => {
    if (error || !token) {
      reject(error)
    } else {
      resolve(token)
    }
  })
  refreshQueue = []
}

/**
 * The session ended while a refresh was in flight (#1081 P2-2).
 *
 * Distinct from a network/401 failure: the rotation itself may well have
 * succeeded server-side, but there is no longer a session here to hand it to.
 * Callers treat it like any other refresh failure -- tear down, do not revive.
 */
export class SessionEndedDuringRefreshError extends Error {
  constructor() {
    super('SESSION_ENDED_DURING_REFRESH')
    this.name = 'SessionEndedDuringRefreshError'
  }
}

/**
 * Did POST /auth/refresh genuinely REFUSE us (H3)?
 *
 * The distinction this whole function exists to draw:
 *
 *   AUTH REJECTION  -- the backend looked at the refresh token and said no.
 *                      401 (invalid / expired / already-rotated), or 403.
 *                      The session really is over; force-logout is correct.
 *   TRANSPORT       -- we never got an answer, or got one that says nothing
 *                      about the token: a Railway restart (502/503), a cold
 *                      start timing out, DNS, offline, a 5xx. The refresh token
 *                      in storage is untouched and probably still valid.
 *
 * Before SESSION-LOGOUT-WHILE-POLLING-01 the interceptor caught both with one
 * `catch` and signed the user out either way, so a backend redeploy logged
 * everyone out mid-session. A Railway restart must never log users out.
 *
 * SessionEndedDuringRefreshError counts as an auth rejection: it means a logout
 * landed while the rotation was airborne, so there is deliberately no session
 * left to keep.
 */
/**
 * Did WE give up, rather than the server answering?
 *
 * axios surfaces an AbortController abort as `ERR_CANCELED` / `CanceledError`.
 * It matters because an abort says nothing about the token: the request may
 * have been served before we stopped listening.
 */
export function isAbort(error: unknown): boolean {
  if (axios.isCancel?.(error)) return true
  if (axios.isAxiosError(error)) {
    return error.code === 'ERR_CANCELED' || error.name === 'CanceledError'
  }
  return (error as { name?: string })?.name === 'AbortError'
}

export function isAuthRejection(error: unknown): boolean {
  if (error instanceof SessionEndedDuringRefreshError) return true
  // Round-1 review P2-1/P2-2/P2-4. None of these is the server saying the token
  // is bad, so none of them may end a session:
  //   * the lock never came free              (someone else is rotating)
  //   * we stopped waiting for the POST       (outcome unknown)
  //   * nobody published inside the wait      (nothing known to be wrong)
  //   * a 200 with an unusable body           (protocol, not auth)
  // They all route to the "reconnecting" branch with the credentials intact.
  if (
    error instanceof RefreshLockTimeoutError
    || error instanceof RefreshAmbiguousError
    || error instanceof RefreshStalledError
    || error instanceof RefreshProtocolError
  ) {
    return false
  }
  if (axios.isAxiosError(error)) {
    const status = error.response?.status
    // No `response` at all => the request never completed: network error,
    // timeout, CORS failure, or the container going away mid-flight. Not a
    // refusal.
    if (status === undefined) return false
    return status === 401 || status === 403
  }
  // NO_REFRESH_TOKEN and anything else non-HTTP: there is nothing to refresh
  // with, which IS the end of the session.
  return true
}

/**
 * Transport-failure retry budget for a single refresh (H3).
 *
 * Deliberately short. It is not trying to outlast a full Railway redeploy --
 * it cannot, and it does not need to: on giving up, the tokens are KEPT and no
 * logout happens, so the next request simply tries again. Its job is only to
 * ride out the common blip (a cold start, a dropped packet) without bubbling a
 * failure into the caller at all.
 */
const REFRESH_TRANSPORT_RETRIES = 2
const REFRESH_TRANSPORT_BACKOFF_MS = [1_000, 2_000]

/**
 * Hard ceiling on ONE POST /auth/refresh (round-1 review P2-2).
 *
 * The call had no timeout at all. axios without `timeout` waits as long as the
 * browser will, so a wedged container could hold a rotation open indefinitely:
 * the lock stays held, every other tab queues behind it, and the user watching
 * a take-off sees nothing at all -- not even "reconnecting", because nothing
 * had failed yet.
 *
 * 12 s is chosen against the thing it has to outlast: a cold Railway container
 * answering /auth/refresh, which is seconds, not tens of seconds. Longer than
 * that is not slowness, it is a hang.
 */
const REFRESH_POST_TIMEOUT_MS = 12_000

/** How long a lock-loser waits for the winner's pair before giving up. */
const ADOPT_WAIT_MS = 8_000
const ADOPT_POLL_MS = 100

/**
 * We never got the lock, and no winner published inside {@link ADOPT_WAIT_MS}.
 *
 * Transport-shaped on purpose: nothing about the SESSION is known to be wrong,
 * so nothing is cleared and nobody is signed out. `isAuthRejection` returns
 * false for it, which routes it to the "reconnecting" branch.
 */
export class RefreshStalledError extends Error {
  constructor() {
    super('REFRESH_STALLED')
    this.name = 'RefreshStalledError'
  }
}

/**
 * A rotation whose outcome we do not know.
 *
 * A timeout is NOT a transport failure like a 502. The request may have reached
 * the backend and rotated the token before we gave up -- in which case our copy
 * is now spent and re-POSTing it earns a 401. Or it never arrived, and our copy
 * is still good. We cannot tell from here, so it gets its own type and its own
 * handling: retried exactly ONCE (the token in storage is re-read, so the retry
 * uses whatever a winner may have published), and if that returns 401 the
 * session really is over and ends honestly.
 *
 * Tracked as a follow-up: a backend reuse-grace window would make this
 * knowable. See the PR body.
 */
export class RefreshAmbiguousError extends Error {
  /** The abort that produced this, kept for logs. Not `Error.cause`: the
   *  project targets a lib without it. */
  readonly reason?: unknown

  constructor(reason?: unknown) {
    super('REFRESH_AMBIGUOUS')
    this.name = 'RefreshAmbiguousError'
    this.reason = reason
  }
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

async function postRefresh(refreshToken: string): Promise<{
  access_token: string
  token_type: string
  refresh_token?: string
}> {
  let lastError: unknown
  for (let attempt = 0; attempt <= REFRESH_TRANSPORT_RETRIES; attempt += 1) {
    try {
      // Use bare axios (NOT `api`) to avoid recursive interceptor on
      // /auth/refresh itself.
      // P2-2: bounded. AbortController rather than axios `timeout` so the
      // socket is actually torn down -- a hung container must not keep the
      // cross-tab lock held behind it.
      const controller = new AbortController()
      const abortTimer = setTimeout(
        () => controller.abort(), REFRESH_POST_TIMEOUT_MS,
      )
      let response
      try {
        response = await axios.post<{
          access_token: string
          token_type: string
          refresh_token?: string
        }>(
          `${API_BASE_URL}/auth/refresh`,
          { refresh_token: refreshToken },
          {
            headers: { 'Content-Type': 'application/json' },
            signal: controller.signal,
          },
        )
      } finally {
        clearTimeout(abortTimer)
      }
      // NOT publishOnline() here (P2-4). A 200 proves the wire works, but this
      // function's caller has not yet looked at the BODY, and `200 {}` is a
      // failed refresh wearing a success's clothes. "Online" is declared once
      // the response is known to contain a usable session -- see
      // rotateAndPublish.
      return response.data
    } catch (error) {
      lastError = error
      // A refusal is final: retrying a token the backend has already rejected
      // just burns requests, and the answer will not change.
      if (isAuthRejection(error)) throw error
      // P2-2: we gave up waiting. The server may or may not have rotated. Stop
      // the transport-retry loop -- those retries assume our token is still
      // good, and after a timeout that is exactly what we do not know.
      if (isAbort(error)) {
        publishReconnecting(attempt + 1)
        throw new RefreshAmbiguousError(error)
      }
      // Transport. Say so -- the user watching a job deserves "reconnecting"
      // rather than a screen that has silently stopped updating.
      publishReconnecting(attempt + 1)
      if (attempt < REFRESH_TRANSPORT_RETRIES) {
        await delay(REFRESH_TRANSPORT_BACKOFF_MS[attempt])
      }
    }
  }
  throw lastError
}

async function performRefresh(): Promise<string> {
  // H2 -- the rotation itself runs inside the CROSS-TAB lock, because the
  // refresh token is shared between tabs and the backend rotates it single-use
  // with no grace window. See lib/refreshLock.ts.
  //
  // This first read is NOT the token we will send. It is the "before" half of a
  // comparison: if what is in storage once we hold the lock differs from this,
  // another tab rotated while we queued, and the token we would have sent is
  // already spent. The token we actually POST is always re-read inside.
  const tokenBeforeLock = getRefreshToken()

  try {
    return await refreshUnderLock(tokenBeforeLock)
  } catch (error) {
    // P2-1: the lock never came free. We do NOT rotate unsynchronised -- that
    // is the bug. Someone else is holding it precisely because they are
    // rotating, so wait for what they publish.
    if (error instanceof RefreshLockTimeoutError) {
      publishReconnecting(1)
      return awaitWinnersPair(tokenBeforeLock)
    }
    // P2-2: the POST timed out; the server may or may not have rotated. Retry
    // exactly ONCE. The retry re-reads storage inside the lock, so if a winner
    // published meanwhile it adopts instead of posting a spent token. A 401
    // from the retry is a real refusal and ends the session honestly.
    if (error instanceof RefreshAmbiguousError) {
      return refreshUnderLock(getRefreshToken())
    }
    throw error
  }
}

function refreshUnderLock(tokenBeforeLock: string | null): Promise<string> {
  return withRefreshLock(async () => {
    const refreshToken = getRefreshToken()
    if (!refreshToken) {
      // Cleared while we waited: a logout landed. Distinguish it from "there
      // was never a token" so the caller tears the session down rather than
      // treating it as a plain configuration error.
      throw tokenBeforeLock
        ? new SessionEndedDuringRefreshError()
        : new Error('NO_REFRESH_TOKEN')
    }

    // ADOPT (H2). We waited for the lock and the token changed while we did:
    // the tab that held it rotated successfully and already published the new
    // pair to storage, this tab's store and every other tab. Posting OUR copy
    // now would send a token the backend revoked seconds ago and earn the 401
    // that used to force-logout a perfectly live session. So we take the
    // winner's access token and never make the call.
    if (tokenBeforeLock !== null && refreshToken !== tokenBeforeLock) {
      const adopted = getAccessToken()
      if (adopted) {
        // P2-3 (round-1 review): ADOPTING IS NOT SILENT.
        //
        // This used to `return adopted` and stop. Storage was correct -- the
        // winner wrote it -- but nothing in THIS tab was told: the zustand
        // store kept the old access token, and any token sink subscribed via
        // authTokenSync got zero notifications. The retried request carried the
        // right header (it reads storage), while the rest of the tab believed
        // it was still on the previous generation.
        //
        // Publish to the LOCAL sinks only. Deliberately not
        // `broadcastAuthEvent`: the winner already broadcast this exact pair,
        // and re-broadcasting it would have every adopting tab echo every other
        // adopting tab. One writer, many local adopters, no loop.
        publishTokenPair({ accessToken: adopted, refreshToken })
        return adopted
      }
      // No access token alongside the new refresh token is not a state the
      // publish path produces; fall through and rotate rather than invent one.
    }

    return rotateAndPublish(refreshToken)
  })
}

/**
 * Wait for the winner to publish, after we failed to get the lock (P2-1/P2-2).
 *
 * The old lease fallback ran an UNSYNCHRONISED rotation when the wait expired,
 * which is the double-POST this whole mechanism exists to prevent. We do the
 * opposite: say "reconnecting" and watch storage for the pair the lock-holder
 * is in the middle of producing. If it lands, we adopt it exactly as the ADOPT
 * branch above would.
 */
async function awaitWinnersPair(
  tokenBeforeLock: string | null,
): Promise<string> {
  const deadline = Date.now() + ADOPT_WAIT_MS
  while (Date.now() < deadline) {
    await delay(ADOPT_POLL_MS)
    const current = getRefreshToken()
    if (!current) throw new SessionEndedDuringRefreshError()
    if (current !== tokenBeforeLock) {
      const adopted = getAccessToken()
      if (adopted) {
        publishTokenPair({ accessToken: adopted, refreshToken: current })
        publishOnline()
        return adopted
      }
    }
  }
  // Still nothing. Tokens are UNTOUCHED and nobody is logged out -- the next
  // request tries again. This is a transport-shaped failure, and the caller's
  // H3 branch treats it as one.
  throw new RefreshStalledError()
}

/**
 * POST the rotation and publish the pair. Runs with the cross-tab lock HELD.
 *
 * The transport retry lives in postRefresh (H3); everything below it is the
 * pre-existing #1081 P2-2 session-generation check, unchanged.
 */
/**
 * A refresh answered 200 but did not answer with a session (P2-4).
 *
 * PRE-EXISTING, and worse than it looks: `200 {}` produced
 * `data.access_token === undefined`, which `setTokens` wrote into localStorage
 * as the STRING "undefined". Every later request then sent
 * `Authorization: Bearer undefined`, `publishOnline()` reported the connection
 * healthy, and the real credentials -- which were still perfectly good -- had
 * been overwritten by a value that can never authenticate.
 *
 * A malformed body is a PROTOCOL failure, not an auth refusal: the server never
 * said the token was bad. So the existing credentials are KEPT, nothing is
 * published, and the caller surfaces "reconnecting" -- the same treatment as a
 * 502, which is what a proxy returning a truncated body effectively is.
 */
export class RefreshProtocolError extends Error {
  constructor(detail: string) {
    super(`REFRESH_PROTOCOL: ${detail}`)
    this.name = 'RefreshProtocolError'
  }
}

function nonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0
}

async function rotateAndPublish(refreshToken: string): Promise<string> {
  const data = await postRefresh(refreshToken)

  // P2-4: validate BEFORE anything is written. BOTH halves must be real,
  // non-empty strings, and both UNCONDITIONALLY (round-2 review P2).
  //
  // Round 1 required `access_token` but let `refresh_token` be absent, because
  // the field is optional in the response type. That optionality is a client-side
  // fiction: POST /auth/refresh rotates SINGLE-USE and always answers with the
  // new pair (api/auth.py refresh_token_endpoint -> Token(..., refresh_token=
  // new_raw_refresh)). So a 200 carrying only an access token is not a lenient
  // success, it is a malformed one -- and it did real damage:
  //
  //   * tokenStorage.setTokens IGNORES a null refresh token, so the token we
  //     just SPENT stayed in localStorage. The server revoked it on the way
  //     through, so the tab was left holding a dead credential;
  //   * publishOnline() had already declared the connection healthy;
  //   * the next rotation POSTs that dead token, earns a 401, and THAT is the
  //     force-logout this whole branch exists to prevent -- arriving one
  //     access-token lifetime later, with no visible cause.
  //
  // A malformed body is a PROTOCOL failure, not an auth refusal: the server never
  // said the token was bad. The working credentials are kept untouched, nothing
  // is published, and the caller surfaces "reconnecting".
  if (!nonEmptyString(data?.access_token)) {
    // Reconnecting, not online: the exchange did not produce a session, and
    // saying "online" here is how the old code reported a healthy connection
    // while writing "undefined" into the Authorization header. Published from
    // HERE rather than only in the interceptor so a SILENT refresh -- which has
    // no interceptor above it -- surfaces the same state.
    publishReconnecting(1)
    throw new RefreshProtocolError('access_token missing or not a string')
  }
  if (!nonEmptyString(data?.refresh_token)) {
    publishReconnecting(1)
    throw new RefreshProtocolError('refresh_token missing or not a string')
  }

  // Now we know it works AND that it gave us a session.
  publishOnline()

  const newAccessToken = data.access_token
  const newRefreshToken = data.refresh_token

  // REVIEW FIX (#1081 P2-2): POST /auth/refresh is a network round-trip, and
  // the session can end or move on while it is airborne. Publishing
  // unconditionally on the way back wrote tokens into storage AND the store
  // AND every other tab -- i.e. a logout that happened mid-flight was silently
  // undone, and TOKENS_UPDATED was broadcast for a session the user had just
  // ended. The refresh token we sent is the session generation: if storage no
  // longer holds it, this result belongs to a session that is no longer ours.
  const currentRefreshToken = getRefreshToken()
  if (currentRefreshToken !== refreshToken) {
    if (!currentRefreshToken) {
      // Cleared: a logout (here or in another tab) landed. Discard the pair
      // unpublished and fail, so the caller tears down rather than revives.
      throw new SessionEndedDuringRefreshError()
    }
    // Changed but still present: another tab rotated first and its pair is
    // the live one. Ours is discarded unpublished -- publishing it would
    // overwrite the winner. The caller retries with the winner's token; if
    // that one is no good either, the next 401 settles it.
    //
    // Caveat, tracked as REFRESH-RETRY-CROSS-SESSION-01: when that other tab
    // signed in a DIFFERENT user, the retry goes out under their session --
    // the request may succeed as B rather than fail as A, which reads oddly in
    // an audit trail. Not a privilege escalation: the server authorises the
    // token actually presented, and the raw keys are shared between tabs
    // either way.
    return getAccessToken() ?? newAccessToken
  }

  publishTokens(newAccessToken, newRefreshToken)
  return newAccessToken
}

/**
 * THE refresh entry point. One in-flight POST /auth/refresh per tab, whoever
 * asks (PROD-SESSION-HARD-NAV-01 / F1.1).
 *
 * Before this existed there were two unsynchronised refresh paths: the
 * interceptor's queue (below) and the auth store's own `isRefreshing` /
 * `lastRefreshAt` gate, which called authApi.refresh with `_skipAuthRefresh`
 * precisely so it would NOT join that queue. On a hard load both fired at once
 * -- the store's silent refresh from onRehydrateStorage, and the interceptor's
 * refresh triggered by the 401 on the boot GET /auth/me/permissions -- each
 * carrying the same raw refresh token. The backend rotates single-use with no
 * grace window (refresh_token_service.rotate_refresh_token), so whichever
 * request arrived second got a 401 "Invalid or expired refresh token"; when
 * that loser was the interceptor it force-logged-out a perfectly valid
 * session. That is the whole hard-navigation bug.
 *
 * Callers that arrive while a refresh is in flight wait for the SAME result
 * instead of starting a second rotation. Rejects with the refresh error when
 * the rotation genuinely failed; the caller decides whether that means
 * force-logout (the interceptor) or a silent give-up (the store).
 */
export async function refreshAccessToken(): Promise<string> {
  if (isRefreshing) {
    return new Promise<string>((resolve, reject) => {
      refreshQueue.push({ resolve, reject })
    })
  }

  isRefreshing = true
  try {
    const newAccessToken = await performRefresh()
    flushQueue(null, newAccessToken)
    return newAccessToken
  } catch (error) {
    flushQueue(error, null)
    throw error
  } finally {
    isRefreshing = false
  }
}

// ============================================================================
// Request Interceptor — attach access token
// ============================================================================

api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getAccessToken()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

// ============================================================================
// Response Interceptor — auto-refresh on 401 + loop protection
// ============================================================================

interface RetryableConfig extends InternalAxiosRequestConfig {
  _retry?: boolean
  _skipAuthRefresh?: boolean
  /** See the module augmentation below. */
  background?: boolean
}

// ----------------------------------------------------------------------------
// `background: true` (SESSION-LOGOUT-WHILE-POLLING-01 / H4)
// ----------------------------------------------------------------------------
// Declared on axios's own config type so call sites can pass it inline --
// `api.get(url, { background: true })` -- and still type-check.
//
// It marks a request the USER IS NOT WAITING ON: a repeating poll of a job that
// runs server-side, which the UI watches but nobody is blocked by. Such a
// request may fail. What it may NOT do is end the session on everyone's behalf.
//
// Before this flag, the QTO cockpit's 2-second poll went through the same 401
// handling as a button click, so a single failed refresh -- one lost cross-tab
// rotation race, one Railway restart -- tore down localStorage, broadcast
// LOGGED_OUT to every tab, and hard-navigated a user who was mid-take-off to
// /login. The poll already had a correct give-up path of its own (linear
// backoff, three strikes, then `pollAbandoned` so the BOQ tiles stop claiming
// work is in progress); the interceptor simply never let it run, because it
// navigated the page away first.
//
// So a background request now: tries the refresh like anyone else (that is how
// it keeps working across a token rotation), retries on success, and on failure
// REJECTS ITS OWN PROMISE and stops there. No clearAllAuth, no broadcast, no
// navigation. If the session really is dead, the next foreground request -- the
// one the user IS waiting on -- finds out and handles it properly.
// ----------------------------------------------------------------------------
declare module 'axios' {
  export interface AxiosRequestConfig {
    /**
     * Mark a request as background/best-effort: it must never force-logout the
     * session. See SESSION-LOGOUT-WHILE-POLLING-01 / H4 above.
     */
    background?: boolean
  }
}

/**
 * UI language for messages produced outside React (this module has no i18n
 * import on purpose — pulling `@/i18n` here would initialise i18next with the
 * full resource bundle in every test that imports the api client). Reads the
 * `<html lang>` that i18n/index.ts keeps in sync, then the persisted choice.
 */
function currentUiLang(): UiLang {
  try {
    const htmlLang = typeof document !== 'undefined' ? document.documentElement.lang : ''
    if (htmlLang) return htmlLang.toLowerCase().startsWith('en') ? 'en' : 'ar'
    const stored = typeof localStorage !== 'undefined' ? localStorage.getItem('language') : null
    if (stored) return stored.toLowerCase().startsWith('en') ? 'en' : 'ar'
  } catch {
    /* storage unavailable (private mode) — fall through */
  }
  return 'ar'
}

/**
 * PERM-DENIED-AR-01 — mutate a 403 AxiosError in place when its detail is the
 * backend RBAC sentence. Exported for tests via __authInternals.
 */
function localisePermissionDenied(error: AxiosError): void {
  const data = error.response?.data as { detail?: unknown; permission_denied_raw?: string; permission_code?: string } | undefined
  if (!data || typeof data !== 'object') return
  const code = parseMissingPermission(data.detail)
  if (!code) return
  const raw = data.detail as string
  console.debug('[rbac] permission denied by backend:', raw, { url: error.config?.url })
  data.permission_denied_raw = raw
  data.permission_code = code
  data.detail = permissionDeniedMessage(code, currentUiLang())
}

/**
 * BUSINESS-RULE-MSG-AR-01 — mutate a 400/403/409 AxiosError in place when its
 * detail is a known backend business-rule sentence. Raw detail is kept on
 * `business_rule_raw` and logged at debug level. Unknown details untouched.
 */
function localiseBusinessRule(error: AxiosError): void {
  const data = error.response?.data as { detail?: unknown; business_rule_raw?: string; business_rule?: string } | undefined
  if (!data || typeof data !== 'object') return
  const rule = matchBusinessRule(data.detail)
  if (!rule) return
  const raw = data.detail as string
  console.debug('[business-rule] rejected by backend:', raw, { rule: rule.id, url: error.config?.url })
  data.business_rule_raw = raw
  data.business_rule = rule.id
  data.detail = businessRuleMessage(rule, currentUiLang())
}

api.interceptors.response.use(
  (response) => {
    // Successful round-trip → reset loop detection counter
    clearLoopHistory()
    // H3 -- any 200 proves the backend is reachable, whichever request earned
    // it. Recovery must not depend on the refresh being the next thing to
    // succeed, or a "reconnecting" banner could outlive the outage.
    publishOnline()
    // H1 -- a `background: true` request that SUCCEEDED is a job the user
    // started telling us it is still alive. The idle timer treats that as
    // presence, but only in a visible tab, and only for as long as the polling
    // continues (it stops the moment the job reaches a terminal status). See
    // lib/backgroundActivity.ts for why HTTP traffic alone is not enough.
    if ((response.config as RetryableConfig | undefined)?.background) {
      publishBackgroundActivity()
    }
    return response
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as RetryableConfig | undefined
    // H4 -- nobody is waiting on this one. It may fail; it may not take the
    // session down with it.
    const isBackground = originalRequest?.background === true

    // PERM-DENIED-AR-01: a 403 whose detail is the backend's RBAC sentence
    // ("Missing required permission: invoice.approve") is rewritten to a
    // localised message so every toast that reads `detail` (directly or via
    // getErrorMessage) shows Arabic/English instead of raw English. The raw
    // detail is kept on `permission_denied_raw` and logged at debug level.
    // 401s and every other 403 are untouched.
    //
    // BUSINESS-RULE-MSG-AR-01: the same normaliser also maps fixed backend
    // business-rule sentences on 400/403/409 (table in lib/permissionLabels.ts,
    // e.g. segregation of duties). Unknown details pass through unchanged.
    const status = error.response?.status

    // AI-CLOUD-KILL-SWITCH-01: one shared handler, not twenty pages.
    // The backend answers every refused paid-AI call with
    // 503 {detail: "AI features are disabled", code: "ai_cloud_disabled"}.
    // Rewriting `detail` here means every existing toast and every
    // getErrorMessage() caller already shows the honest state -- no page needs
    // to know the code exists. `aiCloudDisabled` is set on the error too, for
    // the few places that want to render an inline panel instead of a toast.
    const body = error.response?.data as
      | { detail?: string; code?: string }
      | undefined
    if (status === 503 && body?.code === 'ai_cloud_disabled') {
      const isAr =
        typeof document !== 'undefined' &&
        document.documentElement.lang?.startsWith('ar')
      ;(error as AxiosError & { aiCloudDisabled?: boolean }).aiCloudDisabled =
        true
      if (error.response) {
        error.response.data = {
          ...body,
          detail: isAr
            ? 'ميزات الذكاء الاصطناعي معطّلة على هذا النظام.'
            : 'AI features are disabled on this deployment.',
        }
      }
      return Promise.reject(error)
    }

    if (status === 400 || status === 403 || status === 409) {
      if (status === 403) localisePermissionDenied(error)
      localiseBusinessRule(error)
      return Promise.reject(error)
    }

    // Not a 401 OR no config → just propagate
    if (!originalRequest || error.response?.status !== 401) {
      return Promise.reject(error)
    }

    // Opt-out flag (auth endpoints themselves set this to avoid refresh recursion)
    if (originalRequest._skipAuthRefresh) {
      return Promise.reject(error)
    }

    // Already retried once → give up
    if (originalRequest._retry) {
      return Promise.reject(error)
    }

    const refreshToken = getRefreshToken()

    // H4 -- "there is no refresh token" is a verdict on the SESSION, and a poll
    // nobody is waiting on is not entitled to deliver it. Reject and let the
    // caller's own give-up path run; the next foreground request will reach the
    // branch below and handle it properly.
    //
    // Note this guard sits here and NOT above the refresh attempt: a background
    // request must still be allowed to refresh, or the cockpit would abandon
    // its watch every time the access token rotated under a throttled tab.
    // What it may not do is act on the refresh FAILING.
    if (!refreshToken && isBackground) {
      return Promise.reject(error)
    }

    // PART_4_LOOP_FIX: No refresh token → always clear FIRST, then redirect
    // with loop detection. Using replace() instead of href to prevent history
    // poisoning and back-button loops.
    if (!refreshToken) {
      // Always clear stale auth state to prevent the interceptor from re-attaching
      // the broken access_token to subsequent re-render API calls
      clearAllAuth()
      // clearAllAuth() just wiped the shared token storage, so every other tab
      // is already logged out whether it knows it or not. Say so, instead of
      // leaving them to discover it on their next 401 while still rendering a
      // filtered nav from the previous session's permission set. (The loop-guard
      // branch below re-broadcasts via forceLogoutToLoginPage; the handler is
      // idempotent, so a duplicate is harmless.)
      broadcastAuthEvent({ type: 'LOGGED_OUT' })

      // Loop guard: 3+ redirects within 2 sec → forced single redirect with replace()
      if (recordRedirectAttempt()) {
        forceLogoutToLoginPage()
        return Promise.reject(error)
      }

      // Normal path: single redirect with replace (no back-button loop)
      if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
        window.location.replace('/login')
      }
      return Promise.reject(error)
    }

    originalRequest._retry = true

    // Single-flight: the first 401 rotates, every other 401 in the same burst
    // waits for that one result and retries with it. The queueing that used to
    // be inlined here now lives in refreshAccessToken() so the auth store
    // shares it (PROD-SESSION-HARD-NAV-01 / F1.1).
    try {
      const newAccessToken = await refreshAccessToken()
      originalRequest.headers.Authorization = `Bearer ${newAccessToken}`
      return api(originalRequest)
    } catch (refreshError) {
      // H4 -- a poll nobody is waiting on does not get to end the session, however
      // the refresh failed. Reject, and the cockpit's own linear backoff /
      // three-strike / `pollAbandoned` path takes it from here.
      if (isBackground) {
        return Promise.reject(refreshError)
      }

      // H3 -- WHY did the refresh fail?
      //
      // This used to be one unconditional `forceLogoutToLoginPage()`, which
      // made a Railway restart, a cold start, a 502 and three seconds of lost
      // Wi-Fi indistinguishable from "your refresh token is dead" -- and signed
      // out users whose tokens were perfectly valid.
      //
      // A genuine auth rejection (401/403 from /auth/refresh, or a logout that
      // landed mid-rotation) still ends the session: the server has spoken, and
      // keeping dead tokens around only produces a retry loop.
      if (isAuthRejection(refreshError)) {
        // Idempotent: concurrent waiters all land here, one redirect happens.
        forceLogoutToLoginPage()
        return Promise.reject(refreshError)
      }

      // Transport failure. The tokens are UNTOUCHED and probably still good, so
      // nothing is cleared and nobody is navigated. postRefresh() has already
      // retried with backoff and published the 'reconnecting' state; the banner
      // is up, and the next request (this one's caller retrying, a poll tick, a
      // click) will try again and clear it on the first 200. A Railway restart
      // must never log users out.
      publishReconnecting(REFRESH_TRANSPORT_RETRIES + 1)
      return Promise.reject(refreshError)
    }
  }
)

// ============================================================================
// Helpers (preserved from original)
// ============================================================================

export function getErrorMessage(error: unknown): string {
  if (error instanceof AxiosError) {
    const detail = error.response?.data?.detail

    if (typeof detail === 'string') {
      return detail
    }

    if (Array.isArray(detail) && detail.length > 0) {
      return detail[0].msg || 'حصل خطأ'
    }

    // STRUCTURED details (PV-PERIOD-LOCK-PENDING-01, and the older
    // ap_payment_voucher `{reason, message, message_ar}` shape): the backend
    // ALREADY localised these, and they carry the actionable part of the
    // message — the counts, the reason code. Before this branch every one of
    // them fell through to the generic "حصل خطأ في الاتصال", i.e. the toast
    // said "connection error" for a deliberate, fully-worded business refusal.
    // Arabic first (this is an Arabic-first UI), then the English twin.
    if (detail && typeof detail === 'object') {
      const d = detail as Record<string, unknown>
      for (const key of ['message_ar', 'message', 'message_en'] as const) {
        const value = d[key]
        if (typeof value === 'string' && value.trim()) return value
      }
    }

    return error.message || 'حصل خطأ في الاتصال'
  }

  return 'حصل خطأ غير معروف'
}

// ============================================================================
// Internal exports for Part 5/6 integration + future tests
// ============================================================================

export const __authInternals = {
  STORAGE_KEYS,
  LOOP_DETECTION,
  getAccessToken,
  getRefreshToken,
  setTokens,
  clearAllAuth,
  forceLogoutToLoginPage,
  // PROD-SESSION-HARD-NAV-01 (F1): the auth store reaches the shared
  // single-flight through here, because it cannot be imported from this
  // module (cycle). Its same-tab token listener goes through
  // lib/authTokenSync instead, which it can import at module scope.
  refreshAccessToken,
  // PERM-DENIED-AR-01 (B29 Lane A)
  localisePermissionDenied,
  // BUSINESS-RULE-MSG-AR-01
  localiseBusinessRule,
  currentUiLang,
  // SESSION-LOGOUT-WHILE-POLLING-01 / H3: exported so tests can pin the
  // 401-vs-502 classification directly, without driving a whole interceptor
  // round-trip to infer it.
  isAuthRejection,
}